/** @format */

import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Route, Routes, useNavigate, useLocation, useMatch } from "react-router-dom";
import clsx from "clsx";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { SURVEY } from "./constants/constants";
import SurveySummary from "./SurveySummary";
import SurveyIndividual from "./SurveyIndividual";
import useGetAllSurvey from "./hooks/useGetAllSurvey";
import ThreeDotsLoader from "../common/ThreeDotsLoader";
import * as XLSX from "xlsx";

const SurveyReport = (props) => {
  const { data, isLoading } = useGetAllSurvey();
  let navigate = useNavigate();
  let location = useLocation();
  let isSurveyReportMatch = useMatch("/survey/report");
  let [surveyFilter, setSurveyFilter] = useState({
    selectedSurvey: undefined,
    surveyType: SURVEY.TYPE.SUMMARY,
  });

  const componentRef = useRef();

  let { pathname } = location;
  let { selectedSurvey = {} } = surveyFilter;
  let { status, surveyId } = selectedSurvey;
  let { surveyType } = surveyFilter;
  useEffect(() => {
    if (isSurveyReportMatch?.pattern?.end && selectedSurvey && Object.keys(selectedSurvey).length !== 0) {
      setSurveyFilter((prev) => ({
        ...prev,
        selectedSurvey: undefined,
      }));
      return;
    }
    if (data) {
      let splitPaths = pathname.split("/");
      let pathSurveyId = splitPaths[splitPaths.length - 1];
      let currentSurvey = null;
      if (isNaN(parseInt(surveyId, 10)) && !isNaN(parseInt(pathSurveyId, 10))) {
        currentSurvey = data.find((d) => parseInt(d.surveyId, 10) === parseInt(pathSurveyId, 10));
      }
      if (currentSurvey) {
        let isIndividualPath = pathname.indexOf("individual") !== -1;
        setSurveyFilter((prev) => ({
          selectedSurvey: currentSurvey,
          surveyType: isIndividualPath ? SURVEY.TYPE.INDIVIDUAL : SURVEY.TYPE.SUMMARY,
        }));
      }
    }
  }, [pathname, data, setSurveyFilter, surveyId, navigate, isSurveyReportMatch, selectedSurvey]);

  let onSurveyFilterChangeHandler = useCallback(
    (name, value) => {
      setSurveyFilter((prevFilter) => ({
        ...prevFilter,
        [name]: value,
      }));
      if (surveyType === SURVEY.TYPE.INDIVIDUAL) {
        navigate(`individual/${value.surveyId}`);
      } else {
        navigate(`summary/${value.surveyId}`);
      }
    },
    [surveyType, navigate]
  );

  let onFilterChangeHandler = useCallback(
    (name, value) => {
      let activeSurvey = data.find((d) => d.status.toLocaleLowerCase() === SURVEY.STATUS.ACTIVE.toLocaleLowerCase());
      let selectedSurvey = activeSurvey ? activeSurvey : data[0];
      setSurveyFilter((prevFilter) => ({
        selectedSurvey: selectedSurvey,
        [name]: value,
      }));
      if (value === SURVEY.TYPE.SUMMARY) {
        navigate(`summary/${selectedSurvey.surveyId}`);
      } else {
        navigate(`individual/${selectedSurvey.surveyId}`);
      }
    },
    [data, navigate]
  );

  const isActive = useMemo(
    () => status && status.toLocaleLowerCase() === SURVEY.STATUS.ACTIVE.toLocaleLowerCase(),
    [status]
  );
  const isInProgress = useMemo(
    () => status && status.toLocaleLowerCase() === SURVEY.STATUS.IN_PROGRESS.toLocaleLowerCase(),
    [status]
  );
  const isInactive = useMemo(
    () => status && status.toLocaleLowerCase() === SURVEY.STATUS.INACTIVE.toLocaleLowerCase(),
    [status]
  );

  return (
    <>
      {isLoading ? (
        <div className='wtg__loader'>
          <ThreeDotsLoader />
        </div>
      ) : null}
      <div className='grid p-fluid survey__report__container'>
        <div className='field col-3 md:col-3 align-self-end'>
          <label htmlFor='selectedSurvey'>Survey</label>
          <Dropdown
            placeholder='Select Survey'
            optionLabel='title'
            value={surveyFilter.selectedSurvey}
            options={data ?? []}
            onChange={(e) => {
              onSurveyFilterChangeHandler(e.target.name, e.value ?? []);
            }}
            id='selectedSurvey'
            name='selectedSurvey'
          />
        </div>
        <div className='field col-3 md:col-3 align-self-end'>
          <label htmlFor='Status'>Status</label>
          <Dropdown
            value={surveyFilter.surveyType}
            placeholder='Status'
            options={[
              { label: SURVEY.TYPE.SUMMARY, value: SURVEY.TYPE.SUMMARY },
              { label: SURVEY.TYPE.INDIVIDUAL, value: SURVEY.TYPE.INDIVIDUAL },
            ]}
            id='surveyType'
            name='surveyType'
            onChange={(e) => {
              onFilterChangeHandler(e.target.name, e.value ?? []);
            }}
          />
        </div>

        <div className='col-3 md:col-3 col-offset-3 flex justify-content-end'>
          <div className='flex flex-column'>
            <p>
              <label>
                <b>Status:</b>
              </label>
              <span
                className={clsx({
                  survey__active: isActive,
                  survey__inprogress: isInProgress,
                  survey__inactive: isInactive,
                })}
              >
                {surveyFilter.selectedSurvey?.status}
              </span>
            </p>
            <p className='my-2'>
              <label>
                <b>Start Date:</b>
              </label>
              <label>
                <b>{surveyFilter.selectedSurvey?.startDate}</b>
              </label>
            </p>
            <p className='flex'>
              <Button
                className='btn3'
                onClick={() => {
                  window.print();
                }}
                label='Print'
              />
              <Button
                className='btn3'
                onClick={(e) => {
                  if (
                    componentRef.current &&
                    componentRef.current.getCurrentUserData &&
                    componentRef.current.reportType
                  ) {
                    let rows = componentRef.current.getCurrentUserData();
                    if (componentRef.current.reportType === SURVEY.TYPE.SUMMARY) {
                      const wb = XLSX.utils.book_new();
                      const summaryData = rows.surveyQuestions
                        .map((q) =>
                          q.optionRequestDTOS.map(({ optionId, ...rest }, index) => ({
                            Question: index === 0 ? q.name : null,
                            ...rest,
                          }))
                        )
                        .flat();
                      const ws1 = XLSX.utils.json_to_sheet(summaryData);
                      if (!ws1["!merges"]) ws1["!merges"] = [];
                      let startRow = 2;
                      let endRow = -1;
                      rows.surveyQuestions.forEach((row, index) => {
                        if (index > 0) {
                          startRow = endRow + 1;
                        }
                        endRow = row.optionRequestDTOS.length - 1 + startRow;
                        ws1["!merges"].push(XLSX.utils.decode_range(`A${startRow}:A${endRow}`));
                      });

                      const shortAnswers = rows.shortAnswerQuestionDTOS
                        .map((q) => q.answers.map((ans) => ({ question: q.question, answer: ans })))
                        .flat();
                      const ws2 = XLSX.utils.json_to_sheet(shortAnswers);
                      if (!ws2["!merges"]) ws2["!merges"] = [];
                      let ws2StartRow = 2;
                      let ws2EndRow = -1;
                      rows.shortAnswerQuestionDTOS.forEach((row, index) => {
                        if (index > 0) {
                          ws2StartRow = ws2EndRow + 1;
                        }
                        ws2EndRow = row.answers.length - 1 + ws2StartRow;
                        ws2["!merges"].push(XLSX.utils.decode_range(`A${ws2StartRow}:A${ws2EndRow}`));
                      });

                      XLSX.utils.book_append_sheet(wb, ws1, "Summary");
                      XLSX.utils.sheet_add_aoa(ws1, [["Question", "Answer", "Percentage"]], { origin: "A1" });
                      XLSX.utils.book_append_sheet(wb, ws2, "Comments");
                      XLSX.utils.sheet_add_aoa(ws2, [["Question", "Answer"]], { origin: "A1" });
                      XLSX.writeFile(wb, "Summary.xlsx", { compression: true });
                    } else {
                      const worksheet = XLSX.utils.json_to_sheet(rows);
                      const workbook = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
                      XLSX.utils.sheet_add_aoa(worksheet, [["Question", "Answer", "User"]], { origin: "A1" });
                      XLSX.writeFile(workbook, "Report.xlsx", { compression: true });
                    }
                  }
                }}
                label='Download'
              />
            </p>
          </div>
        </div>
      </div>
      {surveyFilter.selectedSurvey ? null : (
        <>
          <div className='divider' />
          <p className='text-center'>Please select a survey to see report</p>
        </>
      )}
      <Routes>
        <Route path='summary/:id' exact element={<SurveySummary ref={componentRef} status={status} />} />
        <Route path='individual/:id' exact element={<SurveyIndividual ref={componentRef} status={status} />} />
      </Routes>
    </>
  );
};

export default SurveyReport;
